import { deleteDb, getValue, openDb, putValue, deleteValue } from './idb';
import {
  PASSWORD_ROUNDS,
  newDataKey,
  open,
  passwordKey,
  randomBase64,
  seal,
  serverKey,
  unwrapDataKey,
  wrapDataKey,
  type Sealed,
} from './vault';

/**
 * The copy of a person's work this browser keeps, one per person (.docs/023).
 *
 * Two databases:
 *
 * - **`poultry.device`**, shared by everyone who uses this browser. It holds
 *   nothing private: this browser's id, and who has a copy here — the names
 *   they sign in with, so an offline sign-in can find the right copy before
 *   any of it can be opened, and when each last reached the server.
 * - **`poultry.person.<id>`**, one per person, everything in it sealed with
 *   their own data key (see `vault.ts`).
 *
 * A copy is **kept at sign-out**: it may hold work not yet sent, and it is
 * sealed, so the next person at the PC cannot read it.
 */

const DEVICE_DB = 'poultry.device';
const DEVICE_VERSION = 1;
const PERSON_VERSION = 1;
const META = 'meta';
const DATA = 'data';

/**
 * How long somebody may keep signing in with no connection: the refresh
 * token's sliding window. Past it nothing could be sent anyway, and signing in
 * offline would only be a way of pretending otherwise.
 */
export const OFFLINE_DAYS = 7;
const DAY_MS = 86_400_000;

export interface KnownPerson {
  userId: string;
  /** What they sign in with, lower-cased: their user ID, and their mobile. */
  logins: string[];
  displayName: string;
  /** When the server last confirmed them here. */
  lastOnlineAt: number;
}

interface Keys {
  salt: string | null;
  rounds: number;
  byPassword: Sealed | null;
  byServer: Sealed | null;
}

/** The person as the screens need them: whatever the server last said. */
export interface PersonSnapshot {
  id: string;
  userId: string;
  mobile?: string | null;
  fullName: string;
  [field: string]: unknown;
}

export class OfflineSignInError extends Error {
  readonly reason: 'not-known-here' | 'too-long' | 'wrong-password';

  constructor(reason: 'not-known-here' | 'too-long' | 'wrong-password', message: string) {
    super(message);
    this.name = 'OfflineSignInError';
    this.reason = reason;
  }
}

async function deviceDb(): Promise<IDBDatabase> {
  return openDb(DEVICE_DB, DEVICE_VERSION, ['device', 'people']);
}

/** This browser's own id, made the first time it is asked for. */
export async function browserId(): Promise<string> {
  const db = await deviceDb();
  try {
    const kept = await getValue<string>(db, 'device', 'id');
    if (kept) return kept;
    const id = uuid();
    await putValue(db, 'device', 'id', id);
    return id;
  } finally {
    db.close();
  }
}

export async function findPerson(login: string): Promise<KnownPerson | null> {
  const wanted = login.trim().toLowerCase();
  if (!wanted) return null;
  const db = await deviceDb();
  try {
    const people = await new Promise<KnownPerson[]>((resolve, reject) => {
      const request = db.transaction('people', 'readonly').objectStore('people').getAll();
      request.onsuccess = () => resolve(request.result as KnownPerson[]);
      request.onerror = () => reject(request.error ?? new Error('Storage failed'));
    });
    return people.find((p) => p.logins.includes(wanted)) ?? null;
  } finally {
    db.close();
  }
}

async function rememberPerson(person: KnownPerson): Promise<void> {
  const db = await deviceDb();
  try {
    await putValue(db, 'people', person.userId, person);
  } finally {
    db.close();
  }
}

/**
 * Removes every copy this browser holds, and its own id. For a PC an
 * administrator has stopped: whatever was on it must not stay readable there.
 */
export async function forgetEverything(): Promise<void> {
  const db = await deviceDb();
  let ids: string[] = [];
  try {
    const people = await new Promise<KnownPerson[]>((resolve, reject) => {
      const request = db.transaction('people', 'readonly').objectStore('people').getAll();
      request.onsuccess = () => resolve(request.result as KnownPerson[]);
      request.onerror = () => reject(request.error ?? new Error('Storage failed'));
    });
    ids = people.map((p) => p.userId);
  } finally {
    db.close();
  }
  for (const id of ids) await deleteDb(personDbName(id));
  await deleteDb(DEVICE_DB);
}

/**
 * A version-4 UUID, which the server wants for a device id. Made from
 * `getRandomValues` because `randomUUID` exists only on a secure page.
 */
function uuid(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function personDbName(userId: string): string {
  return `poultry.person.${userId}`;
}

export class PersonalStore {
  readonly userId: string;
  private readonly db: IDBDatabase;
  private readonly dataKey: CryptoKey;

  private constructor(userId: string, db: IDBDatabase, dataKey: CryptoKey) {
    this.userId = userId;
    this.db = db;
    this.dataKey = dataKey;
  }

  /**
   * Opens this person's copy once the server has confirmed them, making it the
   * first time. `password` is the one just typed, or null on a reload, when
   * nobody types one.
   *
   * The data key is found with the server key, or failing that with the
   * password. Then it is wrapped again for both — so a password changed or
   * reset by an administrator is picked up here, and a later offline sign-in
   * uses the password typed now.
   */
  static async signedInOnline(
    user: PersonSnapshot,
    password: string | null,
    serverKeyBase64: string,
    options: { rounds?: number; now?: number } = {},
  ): Promise<PersonalStore> {
    const rounds = options.rounds ?? PASSWORD_ROUNDS;
    const db = await openDb(personDbName(user.id), PERSON_VERSION, [META, DATA]);
    const server = await serverKey(serverKeyBase64);
    const keys = await getValue<Keys>(db, META, 'keys');

    let dataKey: CryptoKey | null = null;
    if (keys?.byServer) dataKey = await unwrapDataKey(keys.byServer, server).catch(() => null);
    if (!dataKey && password && keys?.byPassword && keys.salt) {
      const byPassword = await passwordKey(password, keys.salt, keys.rounds);
      dataKey = await unwrapDataKey(keys.byPassword, byPassword).catch(() => null);
    }

    const fresh = !dataKey;
    if (!dataKey) {
      // A first sign-in here, or a copy nothing can open any more (the server
      // key changed and the password with it). What it held is unreadable, so
      // it is cleared rather than left as noise.
      dataKey = await newDataKey();
      await clearStore(db, DATA);
    }

    const next: Keys = {
      salt: keys?.salt ?? null,
      rounds: keys?.rounds ?? rounds,
      byPassword: fresh ? null : (keys?.byPassword ?? null),
      byServer: await wrapDataKey(dataKey, server),
    };
    if (password) {
      next.salt = randomBase64(16);
      next.rounds = rounds;
      next.byPassword = await wrapDataKey(dataKey, await passwordKey(password, next.salt, rounds));
    }
    await putValue(db, META, 'keys', next);

    const store = new PersonalStore(user.id, db, dataKey);
    await store.write('user', user);
    await rememberPerson({
      userId: user.id,
      logins: [user.userId, user.mobile ?? ''].map((l) => l.trim().toLowerCase()).filter(Boolean),
      displayName: user.fullName,
      lastOnlineAt: options.now ?? Date.now(),
    });
    return store;
  }

  /**
   * Opens a copy with no connection, from the name and password typed.
   * Refuses, in words a person can act on, when this PC has no copy for that
   * name, when it is too long since the server last saw them, or when the
   * password is wrong.
   */
  static async signInOffline(
    login: string,
    password: string,
    now: number = Date.now(),
  ): Promise<{ store: PersonalStore; user: PersonSnapshot }> {
    const person = await findPerson(login);
    if (!person) {
      throw new OfflineSignInError(
        'not-known-here',
        'You have not signed in on this PC before, so you need a connection the first time.',
      );
    }
    if (now - person.lastOnlineAt > OFFLINE_DAYS * DAY_MS) {
      throw new OfflineSignInError(
        'too-long',
        `You have not been online for more than ${OFFLINE_DAYS} days. Connect to the internet to sign in. Your saved work is kept.`,
      );
    }

    const db = await openDb(personDbName(person.userId), PERSON_VERSION, [META, DATA]);
    const keys = await getValue<Keys>(db, META, 'keys');
    if (!keys?.byPassword || !keys.salt) {
      db.close();
      throw new OfflineSignInError(
        'not-known-here',
        'Sign in once with a connection and your password, and you can sign in here offline after that.',
      );
    }

    const dataKey = await unwrapDataKey(keys.byPassword, await passwordKey(password, keys.salt, keys.rounds)).catch(
      () => null,
    );
    if (!dataKey) {
      db.close();
      throw new OfflineSignInError('wrong-password', 'The password is not right.');
    }

    const store = new PersonalStore(person.userId, db, dataKey);
    const user = await store.read<PersonSnapshot>('user');
    if (!user) {
      db.close();
      throw new OfflineSignInError('not-known-here', 'Nothing is kept on this PC for you yet. Sign in with a connection.');
    }
    return { store, user };
  }

  /** True when `password` opens this copy. For unlocking with no connection. */
  async passwordOpens(password: string): Promise<boolean> {
    const keys = await getValue<Keys>(this.db, META, 'keys');
    if (!keys?.byPassword || !keys.salt) return false;
    const key = await passwordKey(password, keys.salt, keys.rounds);
    return unwrapDataKey(keys.byPassword, key).then(
      () => true,
      () => false,
    );
  }

  async read<T>(name: string): Promise<T | null> {
    const sealed = await getValue<Sealed>(this.db, DATA, name);
    if (!sealed) return null;
    try {
      return await open<T>(this.dataKey, sealed);
    } catch {
      // Unreadable is the same as absent: the next write puts it right.
      return null;
    }
  }

  async write(name: string, value: unknown): Promise<void> {
    await putValue(this.db, DATA, name, await seal(this.dataKey, value));
  }

  async remove(name: string): Promise<void> {
    await deleteValue(this.db, DATA, name);
  }

  close(): void {
    this.db.close();
  }
}

function clearStore(db: IDBDatabase, store: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Storage failed'));
  });
}
